import type {
  EstateProject,
  EstateProjectFormValues,
  EstateProjectGeoJsonPolygon,
  EstateProjectInfoRow,
  EstateProjectMedia,
  EstateProjectUpsertPayload,
} from '@/modules/estate-project/types/estate-project-types'
import sanitizeHtml from 'sanitize-html'

type SelectItem = { id: string; name: string }

export const createDefaultInfoRows = (): EstateProjectInfoRow[] => [
  { key: '', value: '' },
  { key: '', value: '' },
]

export const initialEstateProjectValues = {
  project_name: '',
  project_address: '',
  lat: null as number | null,
  lng: null as number | null,
  polygon_geojson: null as EstateProjectGeoJsonPolygon | null,
  investor: null as SelectItem | null,
  project_information: createDefaultInfoRows(),
  description: '',
  photos: [] as EstateProjectMedia[],
  videos: [] as EstateProjectMedia[],
  logo: null as EstateProjectMedia | null,
}

export const mappingProjectToForm = (project: EstateProject) => {
  const investor = project.owner || project.investor
  const information = project.information || project.project_information || []
  const polygon = project.geojson || project.polygon_geojson

  return {
    project_name: project.name || project.project_name,
    project_address: project.address || project.project_address,
    lat: project.lat,
    lng: project.lng,
    polygon_geojson: polygon,
    investor: investor
      ? {
          id: investor.id,
          name: investor.name,
        }
      : null,
    project_information: information.length > 0 ? information : createDefaultInfoRows(),
    description: project.description,
    photos: project.photos,
    videos: project.videos,
    logo: typeof project.logo === 'string' ? { uuid: project.logo } : (project.logo ?? null),
  }
}

const normalizeGeoJson = (polygon: EstateProjectGeoJsonPolygon) => {
  const firstRing = polygon.coordinates?.[0] || []
  if (firstRing.length < 3) throw new Error('Polygon không hợp lệ')

  const first = firstRing[0]
  const last = firstRing[firstRing.length - 1]
  const isClosed = first && last && first[0] === last[0] && first[1] === last[1]

  if (isClosed) return polygon

  return {
    type: 'Polygon' as const,
    coordinates: [[...firstRing, first]],
  }
}

export const formatEstateProjectToSubmit = (
  values: EstateProjectFormValues,
): EstateProjectUpsertPayload => {
  if (!values.polygon_geojson) throw new Error('Polygon là bắt buộc')
  if (!values.investor?.id) throw new Error('Chủ đầu tư là bắt buộc')

  return {
    name: values.project_name,
    address: values.project_address,
    lat: Number(values.lat),
    lng: Number(values.lng),
    geojson: normalizeGeoJson(values.polygon_geojson),
    owner_id: values.investor.id,
    information: (values.project_information || [])
      .filter((item: EstateProjectInfoRow) => item.key?.trim())
      .map((item: EstateProjectInfoRow) => ({
        key: item.key.trim(),
        value: String(item.value || '').trim(),
      })),
    description: sanitizeHtml(String(values.description || '')),
    photos: (values.photos || []).map((item: EstateProjectMedia) => item.uuid),
    videos: (values.videos || []).map((item: EstateProjectMedia) => item.uuid),
    logo: values.logo?.uuid || null,
  }
}
